import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import ProfileDistributionChart from './ProfileDistributionChart';
import { useProfileDistribution } from '@/hooks/useProfileDistribution';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ProfileDistributionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProfileDistributionModal: React.FC<ProfileDistributionModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [scope, setScope] = useState<'company' | 'all'>('company');
  const [modelFilter, setModelFilter] = useState<'all' | 'model1' | 'model2' | 'model3'>('all');
  const { data: isSuperAdmin } = useSuperAdminCheck();
  const { data, loading, totalCandidates } = useProfileDistribution(scope, modelFilter);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">
            Profile Distribution
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Based on {totalCandidates} candidate{totalCandidates !== 1 ? 's' : ''}
          </p>
          {isSuperAdmin && (
            <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-border">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${scope === 'company' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  This Company
                </span>
                <Switch
                  checked={scope === 'all'}
                  onCheckedChange={(checked) => setScope(checked ? 'all' : 'company')}
                />
                <span className={`text-sm ${scope === 'all' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  All Companies
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Model:</span>
                <Select value={modelFilter} onValueChange={(v) => setModelFilter(v as any)}>
                  <SelectTrigger className="h-8 w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Models</SelectItem>
                    <SelectItem value="model1">M1</SelectItem>
                    <SelectItem value="model2">M2</SelectItem>
                    <SelectItem value="model3">M3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground mb-2">No profile data available</p>
            <p className="text-sm text-muted-foreground">
              Complete assessments to see profile distribution
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Chart */}
            <ProfileDistributionChart data={data} />

            {/* Data Table */}
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Profile
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                      Pattern
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                      Count
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.map((item, index) => (
                    <tr key={index} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="font-medium text-foreground">
                            {item.profileName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-foreground font-medium uppercase text-xs">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                        {item.pattern}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground">
                        {item.count}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground">
                        {item.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDistributionModal;
